import { useQuery } from '@tanstack/react-query';
import { Activity, History } from 'lucide-react';
import { api } from '../../lib/api';
import { formatTimestamp } from '../../lib/format';
import { Card, EmptyState, Pill, Spinner, TBody, THead, TRow, Table } from '../../components/ui';
import { useSession } from '../../lib/session';

/**
 * What has happened to this ONU, and what was done to it.
 *
 * Two different records, and both were being lost. State changes were a card on
 * the overview competing with the light levels for attention, and the result of
 * an action — the warnings a chassis answered with, whether it was rejected —
 * appeared as a notice that vanished on the next click. An operator asked "did
 * that recreate work" ten minutes later had nowhere to look.
 *
 * Kept apart on purpose. A transition is something the network did; an action
 * is something a person did. Reading them as one list invites blaming the
 * second for the first.
 */
interface OnuEvent {
  id: string;
  at: number;
  kind: string;
  fromState: string | null;
  toState: string;
  description: string;
}

interface AuditEntry {
  id: string;
  at: number;
  action: string;
  actorLabel: string;
  detail: Record<string, unknown> | null;
}

export function EventsTab({
  oltId,
  onuId,
  onuRowId,
}: {
  oltId: string;
  onuId: string;
  onuRowId: string;
}) {
  const { can } = useSession();

  const events = useQuery({
    queryKey: ['olt', oltId, 'events', onuId],
    queryFn: () =>
      api.get<{ items: OnuEvent[] }>(
        `/olts/${encodeURIComponent(oltId)}/events?onuId=${encodeURIComponent(onuId)}&limit=200`,
      ),
  });

  // The audit trail is not everyone's to read, so the card is only offered to
  // those who can — an empty panel would read as "nothing was done".
  const canAudit = can('audit.read');
  const actions = useQuery({
    queryKey: ['audit', onuRowId],
    queryFn: () =>
      api.get<{ items: AuditEntry[] }>(
        `/audit?targetId=${encodeURIComponent(onuRowId)}&limit=100`,
      ),
    enabled: canAudit,
  });

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Activity className="size-4 text-muted" />
            State history
            <span className="text-xs font-normal text-faint">
              {events.data?.items.length ?? 0}
            </span>
          </span>
        }
      >
        {events.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : (events.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No state changes recorded"
            description="Events appear as syncs and the subscriber page observe this ONU going down or coming back."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">When</th>
              <th className="th">Change</th>
              <th className="th">Cause</th>
            </THead>
            <TBody>
              {events.data?.items.map((e) => (
                <TRow key={e.id}>
                  <td className="td whitespace-nowrap font-mono text-xs text-muted">
                    {formatTimestamp(e.at)}
                  </td>
                  <td className="td">
                    <Pill state tone={e.kind === 'up' ? 'success' : e.kind === 'down' ? 'danger' : 'warn'}>
                      {e.fromState ? `${e.fromState} → ${e.toState}` : e.toState}
                    </Pill>
                  </td>
                  <td className="td text-sm text-muted">{e.description}</td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {canAudit && (
        <Card
          title={
            <span className="flex items-center gap-2">
              <History className="size-4 text-muted" />
              What was done to it
              <span className="text-xs font-normal text-faint">
                {actions.data?.items.length ?? 0}
              </span>
            </span>
          }
        >
          {actions.isLoading ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : (actions.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="Nothing done to this ONU yet"
              description="Authorising, recreating, rebooting or changing its settings is recorded here, with who did it."
            />
          ) : (
            <Table>
              <THead>
                <th className="th">When</th>
                <th className="th">Action</th>
                <th className="th">Who</th>
                <th className="th">Detail</th>
              </THead>
              <TBody>
                {actions.data?.items.map((a) => (
                  <TRow key={a.id}>
                    <td className="td whitespace-nowrap font-mono text-xs text-muted">
                      {formatTimestamp(a.at)}
                    </td>
                    <td className="td">
                      <Pill state tone={toneFor(a.action)}>{label(a.action)}</Pill>
                    </td>
                    <td className="td text-sm text-muted">{a.actorLabel}</td>
                    <td className="td text-xs text-muted">{summarise(a.detail)}</td>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}

/** The destructive ones read differently at a glance from the routine ones. */
function toneFor(action: string): 'danger' | 'warn' | 'info' | 'neutral' {
  if (/deauthor|delete|reset/.test(action)) return 'danger';
  if (/recreate|reboot|resync/.test(action)) return 'warn';
  if (/authoriz|authoris|management|vlan|port/.test(action)) return 'info';
  return 'neutral';
}

/** `olt.onu_management_mode` is not a sentence. */
function label(action: string): string {
  return action.replace(/^olt\./, '').replace(/_/g, ' ');
}

/**
 * The interesting half of the detail, not the whole object.
 *
 * A row of JSON is unreadable at a glance and the serial is already the page
 * you are on, so those are dropped and what is left is what changed.
 */
function summarise(detail: Record<string, unknown> | null): string {
  if (!detail) return '—';
  const parts = Object.entries(detail)
    .filter(([k, v]) => k !== 'serial' && v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}
